import { useEffect } from "react";
import { useRouter } from "next/router";
import { Routes } from "routes/RouteConstants";
import { useTransactionHistory } from "queries/transactionHistory";
import { useCreditProfile } from "queries/credits/useCreditProfile";
import TransactionHistoryV2Tabs from "./TransactionHistoryV2Tabs";
import TransactionHistoryV2Toolbar from "./TransactionHistoryV2Toolbar";
import TransactionHistoryV2Banner from "./TransactionHistoryV2Banner";
import TransactionHistoryV2Table from "./TransactionHistoryV2Table";
import TransactionHistoryV2MobileBottomActions from "./TransactionHistoryV2MobileBottomActions";
import JhPaginationWrapper from "Tailwind/components/design-packages/JhPaginationWrapper";
import { ALL_TRANSACTIONS, modalStateValues, modalType } from "services/constants";
import LogService from "services/LogService";
import CREDIT_EVENTS from "utils/CreditEvents";
import useCreditProfileStore from "zustandStores/userCreditProfileStore";
import useModalStore from "zustandStores/useModalStore";
import InvoiceEmailModal from "components/InvoiceEmailModal";
import { useIsMobile } from "hooks/useDeviceType";
import EmptyState from "./EmptyState";

const TransactionHistoryV2 = () => {
  const router = useRouter();
  const { query } = router;
  const isMobile = useIsMobile();

  const { modalData, setModalData } = useModalStore(state => ({
    modalData: state.modalData,
    setModalData: state.setModalData
  }));

  const isInvoiceEmailModalOpen =
    modalData?.type === modalType.INVOICE_EMAIL_MODAL &&
    modalData?.state === modalStateValues.open;

  const handleInvoiceEmailModalClose = () => {
    setModalData({
      state: modalStateValues.CLOSE,
      type: ""
    });
  };

  const { tabId = "ALL_TRANSACTIONS", page = 1, dateId = 90 } = query;

  const currentPage = Number(page);

  // Fetch transaction history data
  const {
    data: transactionData,
    isLoading: isTransactionLoading,
    isFetching: isTransactionFetching,
    refetch: refetchTransactions
  } = useTransactionHistory({
    dateId: Number(dateId),
    tabId: tabId as string,
    page: currentPage
  });

  // Use isFetching for skeleton states (includes refetching)
  const showSkeletons = isTransactionLoading || isTransactionFetching;
  // Fetch credit profile data
  const { refetch: refetchCredits } = useCreditProfile();

  const { creditData, showExpiringCredits } = useCreditProfileStore(state => ({
    creditData: state.data,
    showExpiringCredits: state.showExpiringCredits
  }));

  const isReservedTab = tabId === "CREDITS_ON_HOLD";

  // Log landing event
  useEffect(() => {
    if (transactionData && !transactionData.transactionData?.length) {
      LogService.log(CREDIT_EVENTS.TH_NO_TRANSACTION_LANDED);
    }
  }, []);

  useEffect(() => {
    LogService.log(CREDIT_EVENTS.TRANSACTION_HISTORY_LANDED, {
      trackSource: true,
      reservedCredits: creditData?.holdCredits,
      showExpiringCredits: !!showExpiringCredits
    });
  }, []);

  // Tab structure - Use API tabs for History, add Reserved Credits manually
  const apiTabs = transactionData?.tabs || [];
  const tabs = [
    {
      value: ALL_TRANSACTIONS,
      label: "History",
      count: apiTabs.find(tab => tab.id === ALL_TRANSACTIONS)?.count || 0
    },
    {
      value: "CREDITS_ON_HOLD",
      label: "Reserved Credits",
      count: apiTabs.find(tab => tab.id === "CREDITS_ON_HOLD")?.count || 0
    }
  ];

  // Create filter options from API tabs for dropdown
  const tabFilterOptions = [
    ...apiTabs.map(tab => ({
      value: tab.id,
      label: tab.label,
      count: tab.count
    }))
  ];

  const handleTabChange = (newTabId: string) => {
    // Prevent switching to Reserved Credits tab if no hold credits
    if (
      newTabId === "CREDITS_ON_HOLD" &&
      (!creditData?.holdCredits || creditData.holdCredits === 0)
    ) {
      return;
    }

    const queryParams: any = { ...query, tabId: newTabId, page: 1 };

    // Remove date filter if switching to Reserved Credits tab
    if (newTabId === "CREDITS_ON_HOLD") {
      delete queryParams.dateId;
    }
    LogService.log(CREDIT_EVENTS.TH_TAB_CLICKED, { tabId: newTabId });
    router.push({
      pathname: Routes.TRANSACTION_HISTORY,
      query: queryParams
    });
  };

  const handlePageChange = (newPage: number) => {
    const queryParams = { ...query, page: newPage };
    router.push({
      pathname: Routes.TRANSACTION_HISTORY,
      query: queryParams
    });
    LogService.log(CREDIT_EVENTS.TH_PAGE_CLICKED, { page: newPage });
  };

  const handleRefresh = () => {
    LogService.log(CREDIT_EVENTS.TH_REFRESH_CLICKED);
    refetchTransactions();
    refetchCredits();
  };

  const handleFilterSelect = (filter: string) => {
    // Filter selection only affects transaction types, always stay on History tab
    const queryParams: any = {
      ...query,
      page: 1,
      tabId: filter
    };

    router.push({
      pathname: Routes.TRANSACTION_HISTORY,
      query: queryParams
    });

    LogService.log(CREDIT_EVENTS.TH_FILTER_SELECTED, {
      filter: filter,
      tabId: filter
    });
  };

  const handleDateFilterChange = (dateValue: string) => {
    const queryParams: any = { ...query, dateId: dateValue, page: 1 };
    router.push({
      pathname: Routes.TRANSACTION_HISTORY,
      query: queryParams
    });
  };

  const itemsPerPage = 10;
  const totalCount = apiTabs.find(tab => tab.id === tabId)?.count || 0;

  const shouldShowPagination =
    totalCount > itemsPerPage

  // On mobile, show old empty state (no table) when empty
  const isEmpty = !transactionData?.transactionData?.length && !showSkeletons;
  const showMobileEmptyState = isMobile && isEmpty;

  return (
    <div id="th-v2-main-container" className="w-full flex flex-col bg-muted tw-root">
      {/* Tabs */}
      <div id="th-v2-tabs-container" className="w-full py-2.5 border border-x-0 border-t-0 border-b-border bg-white px-4" >
        <TransactionHistoryV2Tabs
          tabs={tabs}
          currentTab={isReservedTab ? "CREDITS_ON_HOLD" : "ALL_TRANSACTIONS"}
          onTabChange={handleTabChange}
          holdCredits={creditData?.holdCredits || 0}
        />
      </div>
      <div id="th-v2-content-wrapper" className="px-4">
        <div
          id="th-v2-content-container"
          className={`w-full flex flex-col gap-4 max-w-[993px] mx-auto min-h-[calc(100vh-126px)]  pt-4 ${isMobile ? "" : "mx-auto w-[993px] "
            }`}
        >
          {/* Toolbar */}
          {!isReservedTab && (
            <TransactionHistoryV2Toolbar
              dateFilter={Number(dateId)}
              onDateFilterChange={handleDateFilterChange}
              onRefresh={handleRefresh}
              isLoading={showSkeletons}
              selectedTab={tabId as string}
              onTabChange={handleTabChange}
              tabOptions={tabFilterOptions}
            />
          )}

          {showMobileEmptyState ? (
            <EmptyState />
          ) : (
            <>
              < TransactionHistoryV2Banner creditProfileData={creditData} />

              < div
                id="th-v2-table-wrapper"
                className={`overflow-x-auto w-full mb-4`}
              >

                <TransactionHistoryV2Table
                  transactionData={transactionData?.transactionData || []}
                  isReservedTab={isReservedTab}
                  onFilterSelect={handleFilterSelect}
                  isLoading={showSkeletons}
                  filterOptions={tabFilterOptions}
                  selectedTab={tabId as string}
                />
              </div>
            </>
          )}

          {/* Pagination */}
          <div id="th-v2-pagination-container" className={`${isMobile && !shouldShowPagination ? "pt-20" : ""}`}>
            {shouldShowPagination && (
              <div id="th-v2-pagination-wrapper" className={`flex w-full mx-auto md:justify-center pb-24 md:pb-4 ${Number(currentPage) > 1 ? "justify-start" : "justify-center pl-10"} overflow-x-auto`}>
                <div id="th-v2-pagination-inner" className={isMobile ? "min-w-fit" : ""}>
                  <JhPaginationWrapper
                    totalCount={totalCount}
                    currentPage={Number(currentPage)}
                    setPage={handlePageChange}
                    itemsPerPage={itemsPerPage}
                    showScrollToTop={true}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Bottom Actions */}
      {
        isMobile && (
          <TransactionHistoryV2MobileBottomActions
            onRefresh={handleRefresh}
            isLoading={showSkeletons}
          />
        )
      }

      {/* Invoice sent to recruiter email popup */}
      {isInvoiceEmailModalOpen && (
        <InvoiceEmailModal
          isOpen={isInvoiceEmailModalOpen}
          onClose={handleInvoiceEmailModalClose}
        />
      )}
    </div >
  );
};

export default TransactionHistoryV2;
